import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, CircularProgress, MenuItem, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TablePagination, TextField, Typography
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TimelineIcon from '@material-ui/icons/Timeline';
import { makeStyles } from '@material-ui/core/styles';
import { InfoButton } from '../FormWidgets/Description';
import getStateManager from '../StateManager';
import DatabaseChart from './Logging/DatabaseChart';
import ReceivedDetails from './Logging/ReceivedDetails';
import Measurements from './Logging/Measurements';
import DashboardRooms from './Logging/DashboardRooms';
import DashboardAlarms from './Logging/DashboardAlarms';
import Events from './Logging/Events';
import HistoryTimeRange from './Logging/HistoryTimeRange';
import HistoryZoom from './Logging/HistoryZoom';
import RecordingControl from './Logging/RecordingControl';
import { databaseCommands as commands, databaseRecords, databaseTimeRange, databaseTimePreset,
  filterDatabaseRecords, databaseMeasurements, databaseSummaryMeasurements, databaseSelectedPeriod, databaseLocalTime, databaseEventZone } from '../Utils/database_history_utils';
import { getHistoryTranslationKey } from '../Utils/history_display_utils';
import { getControllerApiVersion } from '../Utils/utils';
import useDatabaseHistory from './Logging/useDatabaseHistory';
const useStyles = makeStyles((theme) => ({
  panel: { padding: theme.spacing(2), marginBottom: theme.spacing(2), minWidth: 0,
    [theme.breakpoints.down('sm')]: { padding: theme.spacing(1) } },
  fields: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: theme.spacing(2), marginTop: theme.spacing(2),
    '& input[type="datetime-local"]': { colorScheme: theme.palette.type },
    [theme.breakpoints.down('xs')]: { gridTemplateColumns: 'minmax(0, 1fr)' }
  },
  measurementFields: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', '& .MuiFormControl-root': { minWidth: 0 } },
  payload: { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', marginBottom: 0 },
  cell: { verticalAlign: 'top', overflowWrap: 'anywhere' },
  pagination: {
    '& .MuiTablePagination-toolbar': { padding: theme.spacing(0, 1), flexWrap: 'wrap' },
    '& .MuiTablePagination-spacer': { display: 'none' },
    '& .MuiTablePagination-caption': { flex: '1 1 130px', whiteSpace: 'normal' },
    '& .MuiTablePagination-actions': { flexShrink: 0, marginLeft: theme.spacing(1) }
  },
  table: {
    tableLayout: 'fixed',
    [theme.breakpoints.down('xs')]: {
      display: 'block',
      '& thead': { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)' },
      '& tbody': { display: 'block' },
      '& tbody tr': { display: 'block', padding: theme.spacing(1), borderBottom: `1px solid ${theme.palette.divider}` },
      '& tbody td': {
        display: 'grid', gridTemplateColumns: '90px minmax(0, 1fr)', gap: theme.spacing(1),
        padding: theme.spacing(0.5, 1), border: 0,
        '&::before': { content: 'attr(data-label)', fontWeight: 500, fontSize: 12, color: theme.palette.text.secondary }
      },
      '& tbody td:last-child': { display: 'block', '&::before': { display: 'none' } }
    }
  }
}));

function HistorySelect({ label, value, onChange, options, ...props }) {
  return <TextField select fullWidth label={label} value={value}
    SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}
    onChange={(event) => onChange(event.target.value)} {...props}>
    {options.map(([key, text]) => <MenuItem key={key} value={key}>{text}</MenuItem>)}
  </TextField>;
}

const choices = (values, selected = '') => [...new Set([...values, ...(selected ? [selected] : [])])].sort();

export default function LoggingTab() {
  const { id } = useParams();
  const manager = getStateManager();
  const controller = manager.getControllerById(id);
  const controllerSnapshot = useSnapshot(controller);
  const response = controllerSnapshot.database_log_response;
  const controllerApiVersion = getControllerApiVersion(controllerSnapshot);
  const { t } = useTranslation();
  const classes = useStyles();
  const [section, setSection] = useState('dashboard');
  const [selection, setSelection] = useState('main');
  const dashboardContext = useRef(null);
  const [command, setCommand] = useState('update_values');
  const [filters, setFilters] = useState(() => {
    const hours = controller.simulation ? 48 : 24;
    return { zone: 'all', category: 'cur_values', field: '', value: '', ...databaseTimePreset(hours), timeSpan: String(hours), search: '', severity: '', eventType: 'all', actor: '' };
  });
  const [page, setPage] = useState(0);
  const [xAxis, setXAxis] = useState('received');
  const [yAxis, setYAxis] = useState('command');
  const [groupBy, setGroupBy] = useState('zone');
  const [zoomBase, setZoomBase] = useState(null);
  const [includeSensors, setIncludeSensors] = useState(false);
  const periodQuery = useMemo(() => ['update_values', 'events'].includes(command) ? ({
    zone: filters.zone, ...databaseSelectedPeriod({ timeSpan: filters.timeSpan, from: filters.from, until: filters.until }),
    ...(command === 'events' ? { event_type: filters.eventType, actor: filters.actor } : {})
  }) : null, [command, filters.zone, filters.timeSpan, filters.from, filters.until, filters.eventType, filters.actor]);
  const measurementRange = useMemo(() => periodQuery ? ({ start: periodQuery.from, end: periodQuery.to - 1, invalid: false }) : null, [periodQuery]);

  const { rows, summaryData, hasSummary, eventData, knownZones, beforeId, status, failedInserts,
    request, requestDetail, clearResults } = useDatabaseHistory({ manager, controller, id, response,
    command, period: periodQuery, section, includeSensors });
  useEffect(() => { setPage(0); }, [request]);
  useEffect(() => { dashboardContext.current = null; }, [id]);

  const measurements = useMemo(() => {
    if (command !== 'update_values') return [];
    if (hasSummary) return summaryData ? databaseSummaryMeasurements(summaryData) : [];
    return databaseMeasurements(rows);
  }, [rows, command, hasSummary, summaryData]);
  const records = useMemo(() => ['update_values', 'events'].includes(command) ? [] : databaseRecords(rows), [rows, command]);
  const messageText = useMemo(() => new Map(rows.filter((row) => row.payload.command === 'message')
    .map((row) => [row.id, (row.payload.data.message_parts ?? [])
      .map((part) => t(getHistoryTranslationKey({ api_version: row.payload.api_version ?? controllerApiVersion }, part), { defaultValue: part })).join('')])), [rows, controllerApiVersion, t]);
  const filtered = useMemo(() => filterDatabaseRecords(records,
    command === 'message' ? { ...filters, zone: 'all' } : filters).filter((record) => {
    if (command === 'settings_request' && filters.search) {
      return record.row.payload.actor?.username?.toLocaleLowerCase().includes(filters.search.toLocaleLowerCase());
    }
    if (command !== 'message') return true;
    const type = record.row.payload.data.type;
    if (filters.severity && (type === 'information' ? 'info' : type) !== filters.severity) return false;
    return !filters.search || messageText.get(record.row.id)?.toLocaleLowerCase().includes(filters.search.toLocaleLowerCase());
  }), [records, filters, messageText, command]);
  const recordsByRow = useMemo(() => {
    const index = new Map();
    for (const record of filtered) {
      if (!index.has(record.row.id)) index.set(record.row.id, []);
      index.get(record.row.id).push(record);
    }
    return index;
  }, [filtered]);
  const filteredRows = useMemo(() => [...recordsByRow.values()].map((matching) => matching[0].row), [recordsByRow]);
  const zoneNames = useMemo(() => {
    const names = new Map([...Object.entries(knownZones), ...Object.values(controllerSnapshot.zones ?? {}).map((zone) => [String(zone.id), zone.name])]);
    for (const [id, name] of measurements.map((room) => [room.id, room.name])) if (name) names.set(id, name);
    for (const record of records) {
      if (record.name && !names.has(record.zone)) names.set(record.zone, record.name);
    }
    for (const row of rows) {
      if (row.payload.command === 'settings_request' && row.payload.data?.name && !names.has(databaseEventZone(row)))
        names.set(databaseEventZone(row), row.payload.data.name);
    }
    return names;
  }, [records, rows, measurements, knownZones, controllerSnapshot.zones]);
  const currentPage = Math.min(page, Math.max(0, Math.ceil(filteredRows.length / 25) - 1));
  const visibleRows = filteredRows.slice(currentPage * 25, currentPage * 25 + 25);
  const range = databaseTimeRange(filters.from, filters.until);
  const label = (value) => t(`database_command_${value || 'all'}`, { defaultValue: value });
  const categoryLabel = useCallback((value) => value === 'data' ? t('database_fields') :
    t(`database_category_${value}`, { defaultValue: t(value, { defaultValue: value }) }), [t]);
  const fieldLabel = useCallback((value) => value.split('.').map((part, index) =>
    index === 0 ? categoryLabel(part) : t(part, { defaultValue: part })).join(' / '), [categoryLabel, t]);
  const zoneLabel = useCallback((value) => {
    if (value === '') return t('database_unscoped');
    const name = zoneNames.get(value);
    return name || `${t('database_axis_zone')} ${value}`;
  }, [zoneNames, t]);
  const zones = useMemo(() => choices([...zoneNames.keys(), ...(command === 'events' ? ['', ...rows.map(databaseEventZone)] : []), ...records.map((record) => record.zone)], filters.zone === 'all' ? '' : filters.zone), [command, rows, records, filters.zone, zoneNames]);
  const categories = useMemo(() => choices(records.map((record) => record.category), filters.category), [records, filters.category]);
  const fields = useMemo(() => choices(records.filter((record) =>
    (!filters.category || record.category === filters.category) && (filters.zone === 'all' || record.zone === filters.zone))
    .flatMap((record) => Object.keys(record.fields)), filters.field), [records, filters.category, filters.zone, filters.field]);
  const setFilter = (key, value) => {
    setPage(0);
    setFilters((previous) => ({ ...previous, [key]: value,
      ...(['from', 'until'].includes(key) ? { timeSpan: 'custom' } : {}) }));
  };
  const changePeriod = (values) => {
    setPage(0);
    setZoomBase(null);
    setFilters((previous) => ({ ...previous, ...values }));
  };
  const zoomRange = useCallback((from, to) => {
    if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return;
    const start = Math.max(0, Math.floor(from / 60000) * 60000);
    const end = Math.min(start + 180 * 86400000, Math.max(start + 60000, Math.ceil(to / 60000) * 60000));
    setZoomBase((previous) => previous ?? { from: filters.from, until: filters.until, timeSpan: filters.timeSpan });
    setFilters((previous) => ({ ...previous, timeSpan: 'custom', from: databaseLocalTime(start), until: databaseLocalTime(end) }));
  }, [filters.from, filters.until, filters.timeSpan]);
  const refresh = () => {
    if (!['all', 'custom'].includes(filters.timeSpan)) {
      const dates = databaseTimePreset(Number(filters.timeSpan));
      if (dates.until !== filters.until) { changePeriod(dates); if (periodQuery) return; }
    }
    setPage(0);
    request();
  };
  const clearField = () => {
    setFilters((previous) => ({ ...previous, field: '', value: '' }));
    if (xAxis === 'value') setXAxis('received');
    setYAxis('command');
  };
  const selectCommand = (value) => {
    if (value === command) return;
    clearResults();
    setCommand(value);
    setPage(0);
    setFilters((previous) => ({ ...previous, category: value === 'update_values' ? 'cur_values' : '',
      ...(previous.timeSpan === 'all' && ['events', 'update_values'].includes(value) ? { timeSpan: '24', ...databaseTimePreset(24) } : {}),
      field: '', value: '', search: '', severity: '' }));
    setXAxis('received');
    setYAxis('command');
    setGroupBy('zone');
  };
  const summary = (row) => {
    const matching = recordsByRow.get(row.id);
    if (row.payload.command === 'message') return messageText.get(row.id);
    const settings = filters.field ? matching.map((record) =>
      `${zoneLabel(record.zone)}: ${fieldLabel(filters.field)} = ${String(record.fields[filters.field])}`).join(' · ') :
      [...new Set(matching.map((record) => `${zoneLabel(record.zone)} / ${categoryLabel(record.category)}`))].join(' · ');
    if (row.payload.command !== 'settings_request') return settings;
    const actor = row.payload.actor;
    const kind = ['owner', 'subuser'].includes(actor?.kind) ? t(`database_actor_${actor.kind}`) : t('unknown');
    return <>
      <Typography variant="body2">{actor?.username ?? t('unknown')} ({kind})</Typography>
      <Typography variant="body2">{settings}</Typography>
      <Typography variant="caption" color="textSecondary">{t('database_request_unconfirmed')}</Typography>
    </>;
  };
  const entryLabel = (row) => {
    if (command !== 'message') return label(row.payload.command);
    const type = row.payload.data.type === 'information' ? 'info' : row.payload.data.type;
    return t(`database_severity_${type}`, { defaultValue: type ?? t('unknown') });
  };
  const entryHeading = t(command === 'message' ? 'database_severity' : 'database_event_type');
  const isMeasurements = command === 'update_values';
  const isEvents = command === 'events';
  const openExplorer = (zone, measurement) => {
    dashboardContext.current = filters;
    setFilters((previous) => ({ ...previous, zone }));
    setSelection(measurement);
    setSection('explore');
  };
  const changeSection = (next) => {
    if (next === 'dashboard') {
      if (command !== 'update_values') selectCommand('update_values');
      if (dashboardContext.current) setFilters((previous) => ({ ...previous, zone: dashboardContext.current.zone }));
    } else if (section === 'dashboard') dashboardContext.current = filters;
    setSection(next);
  };
  const loadedRange = rows.length ? rows.reduce((acc, row) => [
    Math.min(acc[0], row.received_at), Math.max(acc[1], row.received_at)
  ], [Infinity, -Infinity]) : null;


  return (
    <>
      <Paper className={classes.panel}>
        <Box display="flex" alignItems="center">
          <Typography variant="h6">{t('database_history')}</Typography>
          <InfoButton brief={t('database_history')} content={t('database_workspace_help')} />
          {controller.simulation && <Box ml={2} display="flex" alignItems="center">
            <Typography variant="caption" color="primary">{t('database_demo')}</Typography>
            <InfoButton brief={t('database_demo')} content={t('database_demo_help')} />
          </Box>}
        </Box>
        {!controller.simulation && <RecordingControl key={id} controller={controller} response={response} />}
        <Box display="flex" flexWrap="wrap" mt={1} mb={1} role="group" aria-label={t('database_section')}>
          {[['dashboard', 'database_dashboard'], ['explore', 'database_explore']].map(([value, label]) =>
            <Button key={value} aria-label={t(label)} title={t(label)} color="primary" variant={section === value ? 'contained' : 'text'}
              aria-pressed={section === value} onClick={() => changeSection(value)}>{value === 'dashboard' ? <TimelineIcon /> : t(label)}</Button>)}
        </Box>
        {section === 'explore' && <Box display="flex" flexWrap="wrap" mt={1} role="group" aria-label={t('database_view')}>
          {[['update_values', 'database_view_measurements'], ['events', 'database_events'], ['', 'database_view_raw']].map(([value, text]) =>
            <Button key={value} variant={(value ? command === value : !['events', 'update_values'].includes(command)) ? 'contained' : 'text'} color="primary"
              aria-pressed={value ? command === value : !['events', 'update_values'].includes(command)} onClick={() => selectCommand(value)}>{t(text)}</Button>)}
          <InfoButton brief={t('database_filter')} content={t(isEvents ? 'database_events_help' : command === 'message' ? 'database_messages_hint' :
            command === 'settings_request' ? 'database_requests_hint' :
              isMeasurements ? 'database_sensor_help' : 'database_raw_hint')} />
        </Box>}
        <div className={[classes.fields, isMeasurements && classes.measurementFields].filter(Boolean).join(" ")}>
          {!isMeasurements && !isEvents && <HistorySelect id="database-entry-type" label={t('database_event_type')} value={command}
            onChange={selectCommand}
            options={commands.map((value) => [value, label(value)])} />}
          {isEvents && <><HistorySelect id="database-event-kind" label={t('database_event_type')} value={filters.eventType}
            onChange={(value) => setFilter('eventType', value)} options={['all', 'alarms', 'settings', 'messages'].map((kind) => [kind, t('database_event_' + kind)])} />
            <Autocomplete key={id} id="database-event-actor" freeSolo autoSelect forcePopupIcon
              clearText={t('reset')} closeText={t('close')} openText={t('database_request_actor')}
              options={choices(rows.filter((row) => row.payload.command === 'settings_request')
                .map((row) => row.payload.actor?.username).filter(Boolean), filters.actor)}
              value={filters.actor || null} onChange={(_, value) => setFilter('actor', (value ?? '').trim())}
              renderInput={(params) => <TextField {...params} label={t('database_request_actor')} />} />
          </>}
          {command === 'settings_request' && <TextField id="database-request-actor" label={t('database_request_actor')}
            value={filters.search} onChange={(event) => setFilter('search', event.target.value)} />}
          {command === 'message' ? <>
            <TextField id="database-search" label={t('database_search')} value={filters.search}
              onChange={(event) => setFilter('search', event.target.value)} />
            <HistorySelect id="database-severity" label={t('database_severity')} value={filters.severity}
              onChange={(value) => setFilter('severity', value)}
              options={[[ '', t('database_all') ], ...['info', 'success', 'warning', 'error'].map((value) => [value, t(`database_severity_${value}`)])]} />
          </> : <>
          <HistorySelect id="database-zone" label={t('database_axis_zone')} value={filters.zone} onChange={(value) => setFilter('zone', value)}
            options={[[ 'all', t('database_all') ], ...zones.map((value) => [value, zoneLabel(value)])]} />
          {!isMeasurements && !isEvents && <><HistorySelect id="database-category" label={t('database_category')} value={filters.category}
            onChange={(value) => { setFilter('category', value); clearField(); }}
            options={[[ '', t('database_all') ], ...categories.map((value) => [value, categoryLabel(value)])]} />
          <HistorySelect id="database-field" label={t('database_field')} value={filters.field} onChange={(value) => {
            setPage(0);
            setFilters((previous) => ({ ...previous, field: value, value: '' }));
            if (!value && xAxis === 'value') setXAxis('received');
            setYAxis(value ? 'value' : 'command');
          }} options={[[ '', t('database_all') ], ...fields.map((value) => [value, fieldLabel(value)])]} />
          <TextField id="database-value" label={t('database_value_filter')} value={filters.value} disabled={!filters.field}
            onChange={(event) => setFilter('value', event.target.value)} /></>}
          </>}
          <Box display="flex" alignItems="flex-end"><HistoryTimeRange filters={filters} onChange={changePeriod} allowAll={!periodQuery} /></Box>
        </div>
        {range.invalid && <Typography color="error" role="alert">{t('database_invalid_dates')}</Typography>}
        <Box display="flex" alignItems="center" flexWrap="wrap" mt={2} style={{ gap: 8 }}>
          <Button variant="contained" color="primary" onClick={refresh}>{t('database_load_latest')}</Button>
          {beforeId && <Button disabled={status === 'loading' || rows.length >= 2000} onClick={() => request(beforeId)}>
            {t('database_load_older')}
          </Button>}
          {periodQuery && section === 'explore' && <HistoryZoom range={periodQuery} onChange={zoomRange} canReset={Boolean(zoomBase)} onReset={() => changePeriod(zoomBase)} />}
          {['loading', 'retrying'].includes(status) && <CircularProgress size={24} aria-label={t('loading')} />}
          {status === 'retrying' && <Typography variant="caption" role="status">{t('database_retrying')}</Typography>}
        </Box>
        <Box display="flex" alignItems="center" flexWrap="wrap" mt={1} style={{ gap: 8 }} aria-live="polite">
          <Typography variant="caption" color="textSecondary">{summaryData ? t('database_resolution', { minutes: summaryData.resolution_ms / 60000 }) : t('database_loaded_count', { count: rows.length })}</Typography>
          {summaryData?.coverage?.first != null && <Typography variant="caption" color="textSecondary">
            {[summaryData.coverage.first, summaryData.coverage.last].map((time) => new Date(time).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })).join(' – ')}
          </Typography>}
          {status === 'ok' && summaryData?.projection_error && <Typography color="error" variant="caption">{t('database_projection_delayed')}</Typography>}
          {summaryData?.indexing && <Typography variant="caption">{t('database_indexing')}</Typography>}
          {summaryData?.limited && <Typography variant="caption">{t('database_query_limited')}</Typography>}
          {((isMeasurements && !summaryData) || (isEvents && !eventData)) && status === 'ok' && <Typography variant="caption">{t('database_server_update')}</Typography>}
          {!summaryData && loadedRange && <Typography variant="caption" color="textSecondary">
            {loadedRange.map((time) => new Date(time).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })).join(' – ')}
          </Typography>}
          {beforeId && <Typography variant="caption" color="textSecondary">{t('database_partial_history')}</Typography>}
          {rows.length >= 2000 && <Typography variant="caption" color="textSecondary">{t('database_preview_limit')}</Typography>}
          <InfoButton brief={t('database_coverage')} content={t(summaryData ? 'database_summary_help' : 'database_coverage_help')} />
        </Box>
      </Paper>
      {section === 'dashboard' && <DashboardAlarms controller={controller} />}
      {!['idle', 'loading', 'retrying', 'ok'].includes(status) &&
        <Alert severity="warning" role="alert">{t(`database_status_${status}`)}</Alert>}
      {failedInserts > 0 && <Alert severity="warning">
        {t('database_failed_inserts', { count: failedInserts })}
      </Alert>}
      {section === 'dashboard' && <DashboardRooms controller={controller} catalogue={measurements} zone={filters.zone} range={{ start: periodQuery.from, end: periodQuery.to - 1 }} onExplore={openExplorer} />}
      {section === 'explore' && isMeasurements && measurements.length > 0 && <Measurements catalogue={measurements} zone={filters.zone} range={measurementRange}
        onRangeChange={zoomRange} onDetailRequest={summaryData ? requestDetail : undefined} revision={summaryData?.request_id}
        selection={selection} onSelection={setSelection} showSensors={includeSensors} onShowSensors={setIncludeSensors} />}
      {!isMeasurements && !isEvents && status === 'ok' && filteredRows.length === 0 && <Typography>{t('database_empty')}</Typography>}
      {section === 'explore' && isMeasurements && status === 'ok' && measurements.length === 0 && <Typography>{t('database_empty')}</Typography>}
      {isEvents && <Events rows={rows} range={periodQuery} filters={filters} zoneLabel={zoneLabel} fieldLabel={fieldLabel} messageText={messageText} onRangeChange={zoomRange} />}
      {!isMeasurements && !isEvents && rows.length > 0 && <>
        {!['message', 'settings_request'].includes(command) && <Paper className={classes.panel}>
          <Typography variant="h6">{t('database_chart')}</Typography>
          <div className={classes.fields}>
            <HistorySelect id="database-x-axis" label={t('database_x_axis')} value={xAxis} onChange={setXAxis}
              options={['received', 'source', ...(filters.field ? ['value'] : [])].map((value) => [value, t(`database_axis_${value}`)])} />
            <HistorySelect id="database-y-axis" label={t('database_y_axis')} value={yAxis} onChange={setYAxis}
              options={['command', 'zone', ...(filters.field ? ['value'] : [])].map((value) => [value, t(`database_axis_${value}`)])} />
            <HistorySelect id="database-group" label={t('database_group_color')} value={groupBy} onChange={setGroupBy}
              options={['command', 'zone', 'none'].map((value) => [value, t(`database_axis_${value}`)])} />
          </div>
          <Box mt={2}>
            {command === 'update_values' && !filters.field ? <Typography color="textSecondary">{t('database_choose_reading')}</Typography> :
              <DatabaseChart records={filtered} field={filters.field} xAxis={xAxis} yAxis={yAxis}
                groupBy={groupBy} zoneLabel={zoneLabel} fieldLabel={fieldLabel} />}
          </Box>
        </Paper>}
        {filteredRows.length > 0 && <Paper><TableContainer>
        <Table size="small" aria-label={t('database_history')} className={classes.table}>
          <TableHead><TableRow>
            <TableCell>{t('database_received_at')}</TableCell>
            <TableCell>{entryHeading}</TableCell>
            <TableCell>{t('database_summary')}</TableCell>
            <TableCell>{t('database_payload')}</TableCell>
          </TableRow></TableHead>
          <TableBody>{visibleRows.map((row) => <TableRow key={row.id}>
            <TableCell className={classes.cell} data-label={t('database_received_at')}>
              <div>{new Date(row.received_at).toLocaleString()}</div>
            </TableCell>
            <TableCell className={classes.cell} data-label={entryHeading}><div>{entryLabel(row)}</div></TableCell>
            <TableCell className={classes.cell} data-label={t('database_summary')}><div>{summary(row)}</div></TableCell>
            <TableCell className={classes.cell} data-label={t('database_payload')}>
              <ReceivedDetails payload={row.payload.command === 'settings_request' ? row.payload.data : row.payload}
                labelKey={row.payload.command === 'settings_request' ? 'database_show_requested_settings' : 'database_show_payload'}
                className={classes.payload} />
            </TableCell>
          </TableRow>)}</TableBody>
        </Table>
      </TableContainer>
      <TablePagination component="div" className={classes.pagination} count={filteredRows.length} page={currentPage} rowsPerPage={25} rowsPerPageOptions={[]}
        onPageChange={(_, value) => setPage(value)} nextIconButtonText={t('database_next_page')} backIconButtonText={t('database_previous_page')}
        labelDisplayedRows={({ from, to, count }) => t('database_page_count', { from, to, count })} />
      </Paper>}
      </>}
    </>
  );
}
